import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Footer = () => {
  const [siteConfig, setSiteConfig] = useState({
    phone: "+45 12 34 56 78",
    email: "info@birk-anlaeg.dk",
    cvr: "12345678"
  });

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${API}/config`);
        setSiteConfig(res.data);
      } catch (err) {
        console.error("Error fetching config:", err);
      }
    };
    fetchConfig();
  }, []);

  // Format phone for tel: link (remove spaces)
  const phoneLink = `tel:${siteConfig.phone.replace(/\s+/g, '')}`;
  const emailLink = `mailto:${siteConfig.email}`;

  return (
    <footer data-testid="footer" className="bg-[#1A3C34] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <h3
              className="text-2xl font-bold mb-4"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Birk Anlæg
            </h3>
            <p className="text-gray-300 leading-relaxed">
              Anlægsgartner med erfaring i belægnings- og brolægningsarbejde. 
              Kvalitet, pålidelighed og solidt håndværk i København og omegn.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4
              className="text-lg font-semibold mb-4"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Sider
            </h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/"
                  data-testid="footer-link-home"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Forside
                </Link>
              </li>
              <li>
                <Link
                  to="/ydelser"
                  data-testid="footer-link-services"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Ydelser
                </Link>
              </li>
              <li>
                <Link
                  to="/galleri"
                  data-testid="footer-link-gallery"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Galleri
                </Link>
              </li>
              <li>
                <Link
                  to="/om-os"
                  data-testid="footer-link-about"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Om os
                </Link>
              </li>
              <li>
                <Link
                  to="/kontakt"
                  data-testid="footer-link-contact"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Kontakt
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4
              className="text-lg font-semibold mb-4"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Ydelser
            </h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/ydelser#flisebelaegning"
                  data-testid="footer-service-flisebelaegning"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Flisebelægning
                </Link>
              </li>
              <li>
                <Link
                  to="/ydelser#natursten"
                  data-testid="footer-service-natursten"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Natursten
                </Link>
              </li>
              <li>
                <Link
                  to="/ydelser#rullegraes"
                  data-testid="footer-service-rullegraes"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Rullegræs
                </Link>
              </li>
              <li>
                <Link
                  to="/ydelser#hegn"
                  data-testid="footer-service-hegn"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Hegn
                </Link>
              </li>
              <li>
                <Link
                  to="/ydelser#traefaeldning"
                  data-testid="footer-service-traefaeldning"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Træfældning og beskæring
                </Link>
              </li>
              <li>
                <Link
                  to="/ydelser#rodfraesning"
                  data-testid="footer-service-rodfraesning"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Rodfræsning
                </Link>
              </li>
              <li>
                <Link
                  to="/ydelser#anlaeg"
                  data-testid="footer-service-anlaeg"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Anlægsopgaver
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4
              className="text-lg font-semibold mb-4"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Kontakt
            </h4>
            <ul className="space-y-4">
              <li>
                <a
                  href={phoneLink}
                  data-testid="footer-phone"
                  className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors"
                >
                  <Phone size={18} />
                  {siteConfig.phone}
                </a>
              </li>
              <li>
                <a
                  href={emailLink}
                  data-testid="footer-email"
                  className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors"
                >
                  <Mail size={18} />
                  {siteConfig.email}
                </a>
              </li>
              <li className="flex items-start gap-3 text-gray-300">
                <MapPin size={18} className="mt-1 flex-shrink-0" />
                <div>
                  <span className="block">København og omegn</span>
                  <span className="block text-sm text-gray-400 mt-1">
                    Frederiksberg, Gentofte, Gladsaxe<br />
                    Rødovre, Hvidovre, Herlev<br />
                    Glostrup, Ballerup, Egedal<br />
                    Furesø, Rudersdal, Dragør
                  </span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/20 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()} Birk Anlæg. Alle rettigheder forbeholdes.
          </p>
          <p className="text-gray-400 text-sm" data-testid="footer-cvr">
            CVR: {siteConfig.cvr}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
